/**
 * Photo card for the Photos pane (pane 1 of the 3-pane rework). Shows the
 * thumbnail with a selection ring + status badge + auto-crop trim preview, the
 * filename, and one removable chip per configured print — border variants are
 * DISTINCT chips (small italic "WB" / "HB" tag names the border). Each chip
 * carries a ✎ glyph: clicking the chip body opens the print editor for THAT
 * print; the chip's ✕ removes just that print; the card ✕ removes the whole
 * photo (confirmed by the parent). A tile-level ✎ (only while the photo has NO
 * chips yet) opens the per-image "add a print" editor; a magnifier (hover) or
 * double-click opens the lightbox. On mobile the icon row is always visible.
 * Low-res chips keep normal text and carry a red "!" badge (tooltip explains).
 * A small "multiple crops" tag appears when the photo's sizes crop differently.
 */

import { parseSizeId, type PricingData, type ResolvedSize } from '@rpl/pricing';
import type { CSSProperties, MouseEvent } from 'react';
import { centeredAutoCropRect } from '../../lib/printCheck';
import {
  chipBorderTag,
  chipLabel,
  isBlockedPrint,
  isRatioUnresolved,
  photoHasMixedCrops,
  photoStatus,
  sizeShort,
  trimOverlay,
} from '../../lib/sizePaper';
import type { LocalPhoto, UiPrint } from '../../lib/types';

export interface PhotoTileProps {
  photo: LocalPhoto;
  prints: UiPrint[];
  selected: boolean;
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  onToggle: (e: MouseEvent) => void;
  onOpenCrop: (printId: string) => void;
  /** Open the lightbox for this photo (magnifier / double-click). */
  onZoom: () => void;
  onRemoveChip: (printId: string) => void;
  onRemovePhoto: () => void;
}

// Trim-overlay geometry (darkened strips + dashed boundary) is shared with the
// Review-crops cards — see lib/sizePaper.trimOverlay.

export function PhotoTile({
  photo,
  prints,
  selected,
  pricing,
  catalog,
  onToggle,
  onOpenCrop,
  onZoom,
  onRemoveChip,
  onRemovePhoto,
}: PhotoTileProps) {
  const status = photoStatus(prints);
  const unresolvedRatio = prints.find((p) => isRatioUnresolved(p) && !(p.rotation ?? 0));
  const mixedCrops = photoHasMixedCrops(prints, photo, catalog);

  // Trim preview for the first ratio print that will actually crop. Rotated
  // prints are skipped — their crop rect lives in the rotated space, so the
  // unrotated thumbnail can't draw it truthfully.
  const trimPrint =
    unresolvedRatio ??
    prints.find((p) => p.printCheck === 'autocrop' && p.fitMode === 'crop' && !(p.rotation ?? 0));
  const trimEntry = trimPrint ? catalog.get(trimPrint.sizeId) : undefined;
  const trimRect =
    trimPrint && trimEntry
      ? trimPrint.cropRect ?? centeredAutoCropRect(photo.width, photo.height, trimEntry.inches)
      : null;
  const trim = trimRect ? trimOverlay(trimRect) : null;

  // Item 8: thumbnails are never cropped square — the image CONTAINS at its
  // real ratio, letterboxed on the card bg. The aspect box is also the
  // positioning context for the trim overlay so its percentages stay true.
  const landscapeThumb = photo.width >= photo.height;
  const thumbBoxStyle: CSSProperties =
    photo.width > 0 && photo.height > 0
      ? {
          aspectRatio: `${photo.width} / ${photo.height}`,
          ...(landscapeThumb ? { width: '100%' } : { height: '100%' }),
        }
      : { width: '100%', height: '100%' };

  let badge: { text: string; kind: 'ok' | 'warn' | 'err' } | null = null;
  if (status === 'lowres') badge = { text: '✕ Too low', kind: 'err' };
  else if (status === 'ratio') badge = { text: 'Auto-cropped', kind: 'warn' };
  else if (status === 'ok') badge = { text: '✓ Fits', kind: 'ok' };

  const ringClass = selected
    ? ' sptile--selected'
    : status === 'lowres'
      ? ' sptile--err'
      : status === 'ratio'
        ? ' sptile--warn'
        : '';

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <div
      className={`sptile${ringClass}`}
      data-tile-id={photo.id}
      role="button"
      aria-pressed={selected}
      aria-label={`${selected ? 'Deselect' : 'Select'} ${photo.filename}`}
      tabIndex={0}
      onClick={onToggle}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onZoom(); // the two toggle clicks cancel out — selection is unchanged
      }}
      onMouseDown={(e) => {
        if (e.shiftKey) e.preventDefault(); // no text selection on shift-click
      }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggle(e as unknown as MouseEvent);
        }
      }}
    >
      <div className="sptile__thumb">
        <span className="sptile__imgbox" style={thumbBoxStyle} aria-hidden={!photo.previewUrl}>
          {photo.previewUrl ? (
            <img className="sptile__img" src={photo.previewUrl} alt={photo.filename} draggable={false} />
          ) : (
            <span className="sptile__img sptile__img--empty" />
          )}
          {trim && (
            <>
              {trim.strips.map((s, i) => (
                <span key={i} className="sptile__strip" style={s} aria-hidden />
              ))}
              <span className="sptile__cropline" style={trim.boundary} aria-hidden />
            </>
          )}
        </span>
        <span className={`sptile__check${selected ? ' sptile__check--on' : ''}`} aria-hidden>
          ✓
        </span>
        {badge && <span className={`sptile__badge sptile__badge--${badge.kind}`}>{badge.text}</span>}
        {mixedCrops && (
          <span className="sptile__multicrop" title="Each size has its own crop">
            ✎ multiple crops
          </span>
        )}
        <button
          type="button"
          className="sptile__zoom"
          aria-label={`View ${photo.filename} large`}
          title="View large"
          onClick={(e) => {
            stop(e);
            onZoom();
          }}
          onDoubleClick={stop}
        >
          ⌕
        </button>
        <button
          type="button"
          className="sptile__remove"
          aria-label={`Remove ${photo.filename}`}
          title="Remove photo"
          onClick={(e) => {
            stop(e);
            onRemovePhoto();
          }}
        >
          ✕
        </button>
      </div>

      <span className="sptile__name" title={photo.filename}>
        {photo.filename}
      </span>

      {prints.length > 0 ? (
        <div className="sptile__chips" onClick={stop}>
          {prints.map((p) => {
            const framed = !!p.frameId;
            const borderTag = chipBorderTag(p);
            const blocked = isBlockedPrint(p);
            return (
              <span
                key={p.id}
                className={`spchip${blocked ? ' spchip--blocked' : ''}${framed ? ' spchip--framed' : ''}`}
              >
                <button
                  type="button"
                  className="spchip__body"
                  title={
                    framed
                      ? 'Open this framed print in Customise'
                      : `Open this ${sizeShort(parseSizeId(p.sizeId).size)} print in Customise`
                  }
                  onClick={() => onOpenCrop(p.id)}
                >
                  <span className="spchip__edit" aria-hidden>
                    ✎
                  </span>
                  {framed && (
                    <span className="spchip__mark" aria-hidden>
                      ▣
                    </span>
                  )}
                  {chipLabel(pricing, p)}
                  {borderTag && (
                    <em
                      className="spchip__border"
                      title={borderTag === 'WB' ? 'White border' : 'Handling border'}
                    >
                      {borderTag}
                    </em>
                  )}
                  {p.bw && (
                    <em className="spchip__border" title="Prints in black & white">
                      B&amp;W
                    </em>
                  )}
                  {blocked && (
                    <span
                      className="spchip__alert"
                      role="img"
                      aria-label="Too low for this size"
                      title="Too low for this size. Swap it, remove it, or print anyway."
                    >
                      !
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="spchip__x"
                  aria-label={`Remove ${chipLabel(pricing, p)}`}
                  onClick={() => onRemoveChip(p.id)}
                >
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      ) : null /* owner 2026-07-27: the old hint copy was noise — tapping a
                  thumb makes the flow obvious */}
    </div>
  );
}
